import angular from 'angular';
import _ from 'lodash-es';
import { DEFINED_BY } from '~/scripts/constants/common-kids';
import Form from '~/scripts/services/model/form';
import Query from '~/scripts/services/model/query';
import Node from '~/scripts/services/model/node';
import APIV5 from '~/models/api';
import { createKtableWidgetActionType } from '~/pages/dashboard/models/widget';
import QueryFrom from '~/scripts/services/model/query-from';
import QueryWhere from '~/scripts/services/model/query-where';
import QueryColumn from '~/scripts/services/model/query-column';
import QueryRule from '~/scripts/services/model/query-rule';
import QueryWhereChild from '~/scripts/services/model/query-where-child';
import QueryWhereNode from '~/scripts/services/model/query-where-node';
import QueryWhereRule from '~/scripts/services/model/query-where-rule';
import QueryWhereRules from '~/scripts/services/model/query-where-rules';
import templateModalQueryJson from '~/components/query/query-editor/modal/modal-query-json.html';
import ModalQueryJsonController from '~/components/query/query-editor/modal/modal-query-json.controller';
import template from './ktable.html';
import { settings } from './settings';

/* globals Knitiv, KtableFormConfig */

/**
 * Parent2
 *
 * @typedef {Object} Parent2
 * @property {Array<*>} definedby definedby
 * @property {*} customColumns customColumns
 * @property {*} krepre_mode krepre_mode
 * @property {Query} query query
 * @property {Object.<string, *>} classRepresentations classRepresentations
 * @property {Object.<string, *>} valuesRepresentations valuesRepresentations
 */

/**
 * Parent1
 *
 * @typedef {Object} Parent1
 * @property {*} $parent $parent
 * @property {*} form form
 * @property {*} forms forms
 * @property {*} submissions submissions
 * @property {*} files files
 */

/**
 * @typedef {Object} Component
 * @property {Query} query query
 */

/**
 * @typedef {Object} MyScope The current scope
 * @property {Object} options Options
 * @property {boolean} readOnly readOnly
 * @property {boolean} forms forms
 * @property {Array<*>} submissions submissions
 * @property {Array<*>} files files
 * @property {*} builder builder
 * @property {Component} component component
 * @property {*} $parent $parent
 */

/**
 * @typedef {KtableFormConfig.Scope} Scope The current scope
 */

const fixComponentOptionsActionInsert = (/** @type {any} */ $scope) => {
	if (
		$scope.component.options.actions.insert !== undefined
		&& $scope.component.options.actions.insertCreate === undefined
		&& $scope.component.options.actions.insertExisting === undefined
	) {
		$scope.component.options.actions.insertCreate = $scope.component.options.actions.insert;
		$scope.component.options.actions.insertExisting = $scope.component.options.actions.insert;
		delete $scope.component.options.actions.insert;
	}
};

const setWhere = (
	/** @type {Query} */ query,
	/** @type {any} */ filter,
	/** @type {Node | any} */ node,
) => {
	if (filter && node && !query.findRule((/** @type {QueryRule} */ rule) => rule.value[0].kid === '_KID_')) {
		if (query.version && query.version >= '3.0.0') {
			query.where = new QueryWhereChild();
			query.where.node = new QueryWhereNode({
				kid:  node.kid,
				name: node.getName(),
			});
			query.where.rules?.items.push(new QueryWhereRule({
				field:    'kid',
				operator: 'equal_kid',
				value:    [{ kid: '_KID_' }],
			}));
		}
		else {
			// eslint-disable-next-line unicorn/consistent-destructuring
			query.where = new QueryWhere({
				rules: [
					new QueryRule({
						field:      filter.kid,
						label:      filter.name,
						operator:   'equal_kid',
						path:       [
							{
								kid:  node.kid,
								name: node.getName(),
								flow: undefined,
							},
							{
								kid:        filter.kid,
								name:       filter.name,
								flow:       'down',
								linker_kid: filter.linker_kid || '0',
							},
						],
						type:       'K_NODE;STRING',
						value:      [{ kid: '_KID_' }],
						linker_kid: filter.linker_kid || '0',
					}),
				],
			});

			if (filter.linker_kid !== '0' && query.where.rules[0] instanceof QueryRule) {
				query.where.rules[0].path[1].linker_info = filter.linker_info;
				query.where.rules[0].linker_info = filter.linker_info;
			}
		}
	}
};

const ktableConfig = /* @ngInject */ (/** @type {any} */ KformComponentsProvider) => {
	KformComponentsProvider.register('ktable', {
		/**
		 * @param {import('angular').auto.IInjectorService} $injector
		 * @param {Scope} $scope
		 * @param {import('angular').translate.ITranslateService} $translate
		 * @param {import('angular').ui.bootstrap.IModalService} $uibModal
		 * @param {any} Notification
		 */
		onEdit: /* @ngInject */ (
			$injector,
			$scope,
			$translate,
			$uibModal,
			Notification,
		) => {
			$scope.cols = [];

			$scope.mfFastImportCard = $scope.component.options.actions.fastImport ? '1-1' : '0-1';

			$scope.valuesRepresentations = {};

			fixComponentOptionsActionInsert($scope);

			$scope.$watch('component.query', () => {
				if (!($scope.component.query instanceof Query)) {
					$scope.component.query = new Query($scope.component.query);
				}
			});

			$scope.$watch('component.options.actions.fastImport', () => {
				$scope.mfFastImportCard = $scope.component.options.actions.fastImport ? '1-1' : '0-1';
			});

			$scope.addFrom = object => {
				let isFromExist = false;

				if (!Array.isArray($scope.component.query.from)) {
					$scope.component.query.from = [];
				}

				for (const from of $scope.component.query.from) {
					if (from !== undefined && from.node === object.kid) {
						isFromExist = true;
					}
				}

				if (isFromExist) {
					return Notification.warning({
						title:   $translate.instant('error.title'),
						message: 'Cette classe a déjà été sélectionnée',
					});
				}
				if (object && object.kid) {
					$scope.component.query.from = [];
					return APIV5.instance.objectInfo({ kid: object.kid }, { id: 'ktable.add-from' }).then(data => {
						$scope.component.query.from.push(new QueryFrom({
							node:              object.kid,
							name:              object.name,
							// @ts-ignore
							type:              object.type || object.type_kid,
							inherits_extended: !data.error && data.parent_inherits.nb > 0 ? 0 : -1,
						}));
						$scope.component.query.select.length = 0;
						return $scope.component.query.from;
					}).catch(() => {});
				}

				return false;
			};

			$scope.colGetId = (/** @type {QueryColumn} */ col) => {
				let returnValue;

				if (col && typeof col === 'object' && 'kid' in col) {
					returnValue = col.kid;
					if (col.linker_kid !== '0') {
						returnValue += `|${col.linker_kid}`;
					}
				}
				return returnValue;
			};

			$scope.colGetKey = (/** @type {QueryColumn} */ col) => {
				let index = -1;

				for (const [k, c] of $scope.cols.entries()) {
					if (c.kid === col.kid && c.linker_kid === col.linker_kid) {
						index = k;
					}
				}

				return index;
			};

			$scope.colGetName = (/** @type {QueryColumn} */ col) => {
				let returnValue = col.name;

				// if (!ret) {
				// 	const cs = scope.$new();
				// 	KTranslate.request(cs, col.kid).then(text => {
				// 		col.name = text;
				// 	});
				// }

				if (col.linker_kid !== '0' && col.linker_info) {
					// const csl = scope.$new();
					// KTranslate.request(csl, col.linker_kid).then(text => {
					// 	col.linker_info.name = text;
					// });
					returnValue += ` :: ${col.linker_info.name}`;
				}

				return returnValue;
			};

			$scope.getRepresentations = (/** @type {string | number} */ columnId) => {
				const repre = $scope.representations[columnId];
				if (repre) {
					const all = repre.repre_all;
					const allArray = [];
					for (const [key, value] of Object.entries(all)) {
						allArray.push({
							lang_kid: key,
							name:     value?.name,
							kid:      repre.kid,
						});
					}
					return allArray;
				}
				return [];
			};

			$scope.hasColRepre = () => {
				let returnValue = false;

				if ($scope.node && $scope.node.length === 1) {
					for (const c of $scope.cols) {
						if (c.kid === $scope.node[0].kid) {
							returnValue = true;
						}
					}
				}

				return returnValue;
			};

			$scope.openJSON = () => {
				$uibModal.open({
					template:     templateModalQueryJson,
					controller:   ModalQueryJsonController,
					controllerAs: '$ctrl',
					size:         'lg',
					resolve:      {
						query: () => $scope.component.query.export(),
					},
				});
			};

			$scope.$on('query-class-deleteClass', () => {
				$scope.component.query.reset();
			});

			if (
				$scope.component.query.where instanceof QueryWhereChild
				&& !$scope.component.query.where.node
				&& $scope.component.query.from.length > 0
			) {
				console.warn('attempt to fix query');
				const node = new Node($injector, $scope.component.query.from[0].node);
				node.setName($scope.component.query.from[0].name);
				node.type_kid = $scope.component.query.from[0].type;
				$scope.component.query.where.node = new QueryWhereNode({
					kid:  node.kid,
					name: node.getName(),
					type: node.type_kid,
				});

				if (!$scope.component.query.where.rules) {
					$scope.component.query.where.rules = new QueryWhereRules();
					const rule = new QueryWhereRule({
						field:    'kid',
						operator: 'equal_kid',
						value:    [{ kid: '_KID_' }],
					});
					rule.setNode($scope.component.query.where.node);
					$scope.component.query.where.rules.items.push(rule);
				}
			}

			if ($scope.component.node?.length === 1 && _.isEmpty($scope.component.query)) {
				/** @type {any} */
				const node = $scope.component.node[0];
				(async () => {
					await $scope.addFrom(node);
					if ($scope.component.cols) {
						$scope.component.query.select = [
							new QueryColumn({
								kid:    node.kid,
								hidden: true,
								// @ts-ignore
								root:   true,
								name:   node.name,
								type:   node.type_kid,
							}),
						];

						for (const col of $scope.component.cols) {
							// If the col should be included inside the query
							if (col.includeInQuery === true || !('includeInQuery' in col)) {
								const select = new QueryColumn({
									// @ts-ignore
									kid:        col.kid,
									// @ts-ignore
									linker_kid: col.linker_kid,
									hidden:     false,
									name:       col.name,
									path:       [
										{
											kid:        node.kid,
											name:       node.name,
											flow:       undefined,
											linker_kid: '0',
										},
									],
									// @ts-ignore
									type:       col.type_kid,
								});

								// noinspection JSUnresolvedVariable
								if (col?.settings?.valueRepresentation) {
									// @ts-ignore
									select.node = col.settings.valueRepresentation.kid;
								}

								select.path.push({
									kid:        col.kid,
									name:       col.name,
									flow:       'down',
									linker_kid: col.linker_kid || '0',
								});

								if (col.linker_kid !== '0') {
									// @ts-ignore
									select.linker_info = col.linker_info;
								}

								$scope.component.query.select.push(select);
							}
						}

						if (
							$scope.cols.length === 0
							&& $scope.component.query.select.length > 0
							&& $scope.component.query.select[0].kid === node.kid
						) {
							$scope.component.query.select[0].hidden = false;
						}
					}

					if ($scope.component.filter) {
						setWhere(
							$scope.component.query,
							$scope.component.filter,
							node,
						);
					}
				})();
			}

			// clean from
			$scope.component.query.from = $scope.component.query.from?.map(from => (new QueryFrom({
				inherits_extended: from.inherits_extended,
				name:              from.name,
				node:              from.node,
				type:              from.type,
			})));
		},
		title:      'K-Table',
		template,
		group:      'advanced',
		children:   false,
		selectable: true,
		settings,
		icon:       'fa fa-table',
		views:      [
			{
				name:     'Display',
				template: 'kform/components/ktable/display.html',
			},
			{
				name:     'Query',
				template: 'kform/components/ktable/query.html',
			},
			{
				name:     'Validation',
				template: 'kform/components/ktable/validate.html',
			},
			{
				name:     'API',
				template: 'templates/components/common/api2.html',
			},
			{
				name:     'Conditional',
				template: 'kform/components/common/conditional.html',
			},
		],
		/**
		 *
		 * @param {Scope} $scope
		 * @param {any} NodeService
		 * @param {import('angular').IQService} $q
		 * @param {import('angular').ICompileService} $compile
		 * @param {import('angular').auto.IInjectorService} $injector
		 * @param {import('angular').ILocationService} $location
		 * @param {Knitiv.RootScope} $rootScope
		 * @param {import('angular').ITimeoutService} $timeout
		 */
		controller: /* @ngInject */ (
			$scope,
			NodeService,
			$q,
			$compile,
			$injector,
			$location,
			$rootScope,
			$timeout,
		) => {
			$scope.options.readOnly = $scope.readOnly;

			if ('component' in $scope.$parent.$parent) {
				// @ts-ignore
				$scope.$parent.$parent.definedby = [];
			}

			if (!($scope.component.query instanceof Query)) {
				$scope.component.query = new Query($scope.component.query);
			}
			if ('extra' in $scope.component.options.actions) {
				delete $scope.component.options.actions.extra;
			}
			$scope.ktableOptions = _.cloneDeep($scope.component.options);

			/** @type {Node} */
			let node;
			const parent = $scope.$parent;
			// @ts-ignore
			const form = parent.form || undefined;

			fixComponentOptionsActionInsert($scope);

			if (!$scope.builder && form) {
				// Recherche du link
				$scope.ktableOptions.parent = {
					create: form.create,
					kid:    form.kid,
					isa:    form.isa,
					name:   form instanceof Form ? form.getName() : form.name,
					target: $scope.component.options.target,
				};
			}

			const loadNode = (/** @type {any} */ newNode) => {
				const result = NodeService
					.load(newNode)
					.then((/** @type {Node} */ n) => {
						node = n;
						$scope.component.query = new Query({
							select: [],
							from:   [
								{
									node:              node.kid,
									name:              node.getName(),
									type:              node.type_kid,
									inherits_extended: -1,
								},
							],
							where:  undefined,
							date:   '_NOW_',
						});

						if ('component' in $scope.$parent.$parent) {
							// @ts-ignore
							$scope.$parent.$parent.krepre_mode = node.krepre.mode;
							// @ts-ignore
							$scope.$parent.$parent.definedby = node.link.filter(
								link => link.flow === 'from' && link.relation_kid === DEFINED_BY,
							).map(v => {
								/** @type {any} */
								const returnValue = {
									kid:         v.objnum2_kid,
									name:        v.objname,
									linker_kid:  v.linker_kid,
									linker_info: undefined,
									type_kid:    v.type_kid,
									disabled:    false,
								};

								if (v.linker_kid !== '0') {
									returnValue.linker_info = v.linker_info;
								}

								return returnValue;
							});

							if (!$scope.builder) {
								// @ts-ignore
								$scope.$parent.$parent.query = $scope.component.query;
							}

							// @ts-ignore
							for (const link of $scope.$parent.$parent.definedby) {
								if (form.isa && link.objnum2_kid === form.isa.objid) {
									// setFilter = true;
									$scope.component.filter = link;
									if ($scope.component.options.target) {
										$scope.ktableOptions.parent.target = link.linker_kid;
									}
								}
							}

							setWhere(
								$scope.component.query,
								$scope.component.filter,
								node,
							);
						}
						return true;
					});

				// Don't fetch all the possible representations in real mode
				if (!('submission' in $scope.$parent.$parent)) {
					// eslint-disable-next-line promise/catch-or-return
					result
						.then(() => {
							// @ts-ignore
							const kids = $scope.$parent.$parent.definedby.map(x => x.kid);

							return $q.when(
								Promise.all(kids.map(
									(/** @type {import('@knitiv/api-client-javascript').KNode} */ kid) => APIV5.instance.objectInfo(
										{ kid },
										{ id: 'ktable.load-node.kids-map' },
									)),
								),
							);
						})
						.then((/** @type {Array<import('@knitiv/api-client-javascript').ObjectInfoOutput>} */ repreListResult) => {
							// @ts-ignore
							$scope.$parent.$parent.valuesRepresentations = {};
							// @ts-ignore
							$scope.$parent.$parent.classRepresentations = {};

							for (const nodeClass of repreListResult) {
								const { objid } = nodeClass;

								// @ts-ignore
								// eslint-disable-next-line unicorn/consistent-destructuring
								$scope.$parent.$parent.valuesRepresentations[objid] = Object.values(nodeClass.isa_lang.list);
								// @ts-ignore
								// eslint-disable-next-line unicorn/consistent-destructuring
								$scope.$parent.$parent.classRepresentations[objid] = Object.values(nodeClass.repre.list);
							}

							return true;
						});
				}
				return result.catch((/** @type {any} */ error) => {
					console.error(error);
				});
			};

			if ('definedby' in $scope.$parent.$parent) {
				$scope.$watch('node', (newNode, oldNode) => {
					let newKid = newNode;
					let oldKid = oldNode;
					if (Array.isArray(newNode)) {
						newKid = newNode.length === 1 && 'kid' in newNode[0] ? newNode[0].kid : undefined;
					}

					if (Array.isArray(oldNode)) {
						oldKid = oldNode.length === 1 && 'kid' in oldNode[0] ? oldNode[0].kid : undefined;
					}

					if (newKid !== oldKid) {
						if (newKid) {
							loadNode(newKid);
						}
						else {
							$scope.component.filter = undefined;
							$scope.cols.length = 0;
						}
					}
				}, true);

				$scope.$watch('component.filter', (newWhere, oldWhere) => {
					if (newWhere && !angular.equals(newWhere, oldWhere)) {
						setWhere(
							$scope.component.query,
							$scope.component.filter,
							node,
						);
					}
				});
			}

			/* Extra actions */
			$scope.$eval(async () => {
				const componentActions = $scope.component.actions;

				/** @type {any[]} */
				let extraActions = [];

				/** @type {any[]} */
				let extraFetchedActions = [];

				if (componentActions.length > 0) {
					/** @type {import('@knitiv/api-client-javascript').QueryBuilder<'action' | 'type' | 'request'>} */
					const actionsQuery = APIV5.instance.createQueryBuilder();

					actionsQuery
						.select(selectBuilder => selectBuilder
							.addColumn(columnBuilder => columnBuilder
								.as('action')
								.withTarget('K_NODE;WIDGETACTION'),
							)
							.addColumn(columnBuilder => columnBuilder
								.as('type')
								.withFormat('KID')
								.withTarget('K_NODE;WDGTACTTYP')
								.withPath(pathBuilder => pathBuilder
									.flowDown('K_NODE;WDGTACTTYP'),
								),
							)
							.addColumn(columnBuilder => columnBuilder
								.as('request')
								.withFormat('KID')
								.withTarget('K_NODE;KSYSQUERY')
								.withPath(pathBuilder => pathBuilder
									.flowDown('K_NODE;WIDGET')
									.flowDown('K_NODE;KSYSQUERY'),
								),
							),
						)
						.from(fromBuilder => fromBuilder
							.addElement('K_NODE;WIDGETACTION'),
						)
						.where(whereBuilder => {
							for (const act of componentActions) {
								whereBuilder
									.addRule(ruleBuilder => ruleBuilder
										.equalKid('K_NODE;WIDGETACTION', act.kid),
									);
							}

							return whereBuilder;
						});

					try {
						const queryFeth = await actionsQuery.fetchAll(
							undefined,
							undefined,
							{ id: 'ktable-config.fetch' },
						);
						extraActions = queryFeth.resolved().rows;
					}
					catch (error) {
						console.error(error);
					}
				}

				if (extraActions.length > 0) {
					const promises = [];
					for (const [, action] of extraActions.entries()) {
						// eslint-disable-next-line no-loop-func
						promises.push(async () => {
							const currentAction = action.action[0];
							const type = action.type[0];
							// const request = action.request[0];

							// if (type.kid === 'K_NODE;WDGTACTTYP_SUBQUERY') {
							// 	subqueries.push({
							// 		name: currentAction.name,
							// 		kid: request.kid,
							// 	});
							// }

							// eslint-disable-next-line no-use-before-define
							return createKtableWidgetActionType(
								type.kid,
								// @ts-ignore
								null, // TODO:
								currentAction,
								{
									$compile,
									$injector,
									$location,
									$rootScope,
									$timeout,
								},
							);
						});
					}

					extraFetchedActions = await Promise.all(promises.map(x => x()));
				}

				$scope.widgetActions = extraFetchedActions
			});

			// if (scope.node && 'definedby' in scope.$parent.$parent) {
			// 	let n = scope.node;
			// 	if (Array.isArray(n) && n.length > 0) {
			// 		n = n[0].kid;
			// 	}
			// 	if (typeof n === 'string' && n) {
			// 		loadNode(n);
			// 	}
			// }
		},
	});
};

export default angular.module('app').config(ktableConfig);
